import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { useCallback, useEffect, useRef, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { useCantons } from '../api/cantonsApi';
import { onboardingApplicationQueryOptions, useSaveWorkAreaMutation } from '../api/onboardingApi';
import { CantonCard } from '../components/CantonCard';
import { OnboardingFooter } from '../components/OnboardingFooter';
import { OnboardingStepContent } from '../components/OnboardingStepContent';
import { OnboardingStepError } from '../components/OnboardingStepError';
import { OnboardingStepHeading } from '../components/OnboardingStepHeading';
import { useOnboardingState } from '../state/onboardingContext';
import { getNextStep } from '../steps';

const REGIONS_CONTENT_WIDTH = 1200;

export function RegionsStep(): ReactElement {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { data: cantons } = useCantons();
    const { regions, setRegions } = useOnboardingState();
    const { data: application } = useQuery(onboardingApplicationQueryOptions);
    const saveWorkArea = useSaveWorkAreaMutation();

    const seeded = useRef(false);
    useEffect(() => {
        if (seeded.current) {
            return;
        }
        if (regions.size > 0 || application?.workArea) {
            if (regions.size === 0 && application?.workArea) {
                setRegions(new Set(application.workArea.cantonUuids));
            }
            seeded.current = true;
        }
    }, [application, regions.size, setRegions]);

    const toggleCanton = useCallback(
        (id: string) => {
            setRegions((prev) => {
                const next = new Set(prev);
                if (next.has(id)) {
                    next.delete(id);
                } else {
                    next.add(id);
                }
                return next;
            });
        },
        [setRegions],
    );

    const handleNext = useCallback((): void => {
        saveWorkArea.mutate(
            { cantonUuids: Array.from(regions) },
            {
                onSuccess: () => {
                    const next = getNextStep('regions');
                    if (next) {
                        void navigate({ to: next.path });
                    }
                },
            },
        );
    }, [navigate, regions, saveWorkArea]);

    return (
        <>
            <OnboardingStepContent maxWidth={REGIONS_CONTENT_WIDTH}>
                <Stack spacing={6}>
                    <OnboardingStepHeading
                        title={t('ONBOARDING.REGIONS.TITLE')}
                        description={t('ONBOARDING.REGIONS.DESCRIPTION')}
                    />

                    <OnboardingStepError error={saveWorkArea.error} />

                    <Box
                        component="ul"
                        aria-label={t('ONBOARDING.REGIONS.LIST_ARIA')}
                        sx={{
                            listStyle: 'none',
                            p: 0,
                            m: 0,
                            display: 'grid',
                            gridTemplateColumns: {
                                xs: 'repeat(2, 1fr)',
                                sm: 'repeat(3, 1fr)',
                                md: 'repeat(6, 1fr)',
                            },
                            gap: 0.5,
                        }}>
                        {cantons.map((canton) => (
                            <li key={canton.uuid}>
                                <CantonCard
                                    canton={canton}
                                    checked={regions.has(canton.uuid)}
                                    onToggle={() => toggleCanton(canton.uuid)}
                                />
                            </li>
                        ))}
                    </Box>
                </Stack>
            </OnboardingStepContent>

            <OnboardingFooter
                currentStep="regions"
                nextDisabled={regions.size === 0 || saveWorkArea.isPending}
                onNext={handleNext}
            />
        </>
    );
}
